import { useMutation } from "@tanstack/react-query"
import { psDelete } from "@/lib/powersync/crud"
import { setTrackShownOnMap } from "@/lib/trackDisplayPrefs"
import { deleteTrackWithPoints, type Track } from "@/lib/tracksDb"
import { queryClient } from "@/lib/queryClient"

/**
 * Payload for deleting a track.
 * `projectId` is required for cache updates after rehydration.
 */
export type DeleteTrackPayload = {
  id: string
  projectId: string
}

/**
 * Mutation key for track delete - used for registry lookup.
 * All track deletes share this key; individual items are identified
 * by inspecting mutation.state.variables.id
 */
export const TRACK_DELETE_MUTATION_KEY = ["tracks", "delete"]

/**
 * Check if a track has a pending delete mutation.
 */
export const getIsTrackDeletePending = (trackId: string): boolean => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: TRACK_DELETE_MUTATION_KEY,
    predicate: ({ state }) => {
      const isPendingOrPaused =
        state.status === "pending" || Boolean(state.isPaused)

      if (!isPendingOrPaused) return false

      const variables = state.variables as DeleteTrackPayload | undefined
      return variables?.id === trackId
    },
  })
  return Boolean(mutations.length)
}

/**
 * Cancel and remove any pending delete mutations for a specific track.
 */
export const cancelTrackDelete = (trackId: string): void => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: TRACK_DELETE_MUTATION_KEY,
    predicate: ({ state }) => {
      const variables = state.variables as DeleteTrackPayload | undefined
      return variables?.id === trackId
    },
  })

  for (const mutation of mutations) {
    queryClient.getMutationCache().remove(mutation)
  }
}

/**
 * The mutation function for deleting a track: removes the synced row from
 * PowerSync (its upload queue then deletes it on the server and from every
 * teammate's device) and any copy still in IndexedDB — a recording in progress
 * or one not yet handed over. Either removal is a no-op when the track isn't
 * in that store. Local only, so it works offline.
 * Exported separately so it can be registered in the mutation registry
 * for rehydration after app restart.
 */
export async function trackDeleteFn(
  payload: DeleteTrackPayload,
): Promise<void> {
  await psDelete("tracks", payload.id)
  await deleteTrackWithPoints(payload.id)
  setTrackShownOnMap(payload.id, false)
}

/**
 * Success handler for track delete.
 * Exported so it can be registered in mutation defaults for rehydration.
 */
export const onSuccessTrackDelete = (
  _data: void,
  payload: DeleteTrackPayload,
) => {
  queryClient.invalidateQueries({
    queryKey: ["tracks", "local", payload.projectId],
  })
}

/**
 * Hook for deleting a track.
 * Supports offline persistence and rehydration.
 *
 * Usage:
 * ```
 * const { mutateAsync } = useTrackDelete()
 * mutateAsync({ id: trackId, projectId })
 * ```
 */
export const useTrackDelete = () => {
  return useMutation({
    mutationKey: TRACK_DELETE_MUTATION_KEY,
    mutationFn: trackDeleteFn,
    networkMode: "always",
    onMutate: async (payload: DeleteTrackPayload) => {
      const originalTrack = queryClient.getQueryData<Track>([
        "track",
        payload.id,
      ])

      // Optimistic updates
      if (originalTrack && originalTrack.status !== "completed") {
        queryClient.setQueryData(["activeTrack"], () => null)
      }
      queryClient.setQueryData(["trackPoints", payload.id], () => null)
      queryClient.setQueryData<Track[]>(
        ["tracks", "local", payload.projectId],
        (old) => old?.filter((t) => t.id !== payload.id),
      )
    },
    onSuccess: onSuccessTrackDelete,
  })
}
